import { Component, type ReactNode } from 'react';
import { Container, Center, Stack, Text, Button, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  handleReset = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container size="sm" py="xl">
          <Center mih="40vh">
            <Stack align="center" gap="md">
              <IconAlertTriangle size={48} stroke={1.5} color="var(--mantine-color-red-6)" />
              <Title order={3}>Something went wrong</Title>
              <Text c="dimmed" ta="center" maw={400}>
                An unexpected error occurred. Try refreshing the page or going back.
              </Text>
              <Button onClick={this.handleReset} variant="light">
                Try Again
              </Button>
            </Stack>
          </Center>
        </Container>
      );
    }

    return this.props.children;
  }
}
